import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '@/hooks/use-debounced-value.ts'
import { useTRPC } from '@/trpc/react.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

type Kind = 'customer' | 'salesperson' | 'organization' | 'business'

function useBillFilterOption(
  kind: Kind,
  purpose: 'bill.read' | 'bill.group.read' | 'money.read',
  selectedId?: number | number[]
) {
  const trpc = useTRPC()
  const [keyword, setKeyword] = useState('')
  const debouncedKeyword = useDebouncedValue(keyword)
  const query = useQuery({
    ...trpc.bill.filterOptions.queryOptions({
      kind,
      purpose,
      keyword: debouncedKeyword || undefined,
      selectedIds: Array.isArray(selectedId) ? selectedId.filter((id) => id > 0) : undefined,
      selectedId: typeof selectedId === 'number' && selectedId > 0 ? selectedId : undefined
    }),
    staleTime: 60_000
  })
  return {
    options: query.data ?? [],
    loading: query.isFetching,
    ...proRemoteSearch(setKeyword)
  }
}

export function useBillFilterOptions(
  selected: {
    businessId?: number | number[]
    customerId?: number | number[]
    salespersonId?: number | number[]
    organizationId?: number | number[]
  },
  purpose: 'bill.read' | 'bill.group.read' | 'money.read' = 'bill.read'
) {
  const customer = useBillFilterOption('customer', purpose, selected.customerId)
  const salesperson = useBillFilterOption('salesperson', purpose, selected.salespersonId)
  const organization = useBillFilterOption('organization', purpose, selected.organizationId)
  const business = useBillFilterOption('business', purpose, selected.businessId)
  return { customer, salesperson, organization, business }
}
